import { Badge } from "@babackapp/ui/components/badge";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  FloatingBar,
  FloatingBarGroup,
} from "@babackapp/ui/components/floating-panel";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { cn } from "@babackapp/ui/lib/utils";
import { DesktopIcon, MobileIcon } from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { ChevronDown, Code2Icon } from "lucide-react";
import { useMemo } from "react";
import { useEmailEditorLocale } from "../context/templates-editor-locale-context";
import { useTemplatesEditorViewport } from "../context/templates-editor-viewport-context";
import { LocaleStateIcon } from "./locale-state-icon";
import { TranslateTemplateDialog } from "./translate-template-dialog";

function LanguageSwitcher() {
  const {
    currentLocale,
    setCurrentLocale,
    availableLocales,
    defaultLocale,
  } = useEmailEditorLocale();

  const { t } = useTranslation("trackoffice");
  const { state, label } = useMemo(() => {
    const locale = availableLocales.find(
      (locale) => locale.value === currentLocale,
    );
    return {
      label: locale
        ? `${locale.label} ${locale.flag}`
        : currentLocale.toUpperCase(),
      state: locale ? locale.state : "empty",
    };
  }, [availableLocales, currentLocale]);

  const sortedLocales = useMemo(() => {
    // Sort locales: default first, then by state (full > partial > empty)
    const stateOrder = { full: 0, partial: 1, empty: 2 };

    return [...availableLocales].sort((a, b) => {
      if (a.value === defaultLocale) return -1;
      if (b.value === defaultLocale) return 1;

      return stateOrder[a.state] - stateOrder[b.state];
    });
  }, [availableLocales, defaultLocale]);

  return (
    <div className="flex items-center gap-2 px-1">
      <DropdownMenu>
        <ContextualTipsTrigger
          tips={[
            {
              id: "template-variable-placeholder",
              title: t(
                "templatesEditor.tips.templateLocales.title",
                "Select the current language to edit",
              ),
              description: t(
                "templatesEditor.tips.templateLocales.description",
                "In order to work properly, your template must at least be translated in the default language. Any missing translation will automatically fallback to it. Select any block in the editor to get information about its translation state.",
              ),
            },
          ]}
        >
          <DropdownMenuTrigger className="text-xs leading-tight h-auto font-medium text-muted-foreground inline-flex items-center gap-1 py-0">
            {label} <LocaleStateIcon state={state} />
            <ChevronDown className="size-4" />
          </DropdownMenuTrigger>
        </ContextualTipsTrigger>
        <DropdownMenuContent className="w-52">
          <ScrollArea className="max-h-56 overflow-y-auto">
            {sortedLocales.map(({ value, label, flag, state }) => (
              <DropdownMenuItem
                key={value}
                onClick={() => setCurrentLocale(value)}
              >
                <span className="flex-1 whitespace-nowrap">
                  {label} {flag}
                </span>
                {value === defaultLocale && (
                  <Badge variant="secondary" className="text-xxs h-4 px-1">
                    {t("templatesEditor.locale.default", "default")}
                  </Badge>
                )}
                <LocaleStateIcon state={state} />
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
      <TranslateTemplateDialog locale={currentLocale} />
    </div>
  );
}

type ViewportSwitcherProps = {
  value: "desktop" | "mobile" | "code";
  onChange: (value: "desktop" | "mobile" | "code") => void;
};

function ViewportSwitcher({ value, onChange }: ViewportSwitcherProps) {
  const getLeftPosition = () => {
    if (value === "desktop") return "left-0";
    if (value === "mobile") return "left-8";
    return "left-16";
  };

  return (
    <div className="">
      <div className="relative flex items-center group">
        <div
          className={cn(
            "absolute group-active:shadow-xs group-active:scale-[0.8] transition-all ease-in-out z-0 w-8 h-5 bg-card shadow-md rounded-full",
            getLeftPosition(),
          )}
        />
        <button
          onClick={() => onChange("desktop")}
          className="z-10 w-8 h-4 rounded-full flex items-center justify-center"
        >
          <DesktopIcon
            className={cn(
              "size-4 opacity-50 transition-all",
              value === "desktop" && "opacity-100",
            )}
          />
        </button>
        <button
          onClick={() => onChange("mobile")}
          className="z-10 w-8 h-4 rounded-full flex items-center justify-center"
        >
          <MobileIcon
            className={cn(
              "size-4 opacity-50 transition-all",
              value === "mobile" && "opacity-100",
            )}
          />
        </button>
        <button
          onClick={() => onChange("code")}
          className="z-10 w-8 h-4 rounded-full flex items-center justify-center"
        >
          <Code2Icon
            className={cn(
              "size-4 opacity-50 transition-all",
              value === "code" && "opacity-100",
            )}
          />
        </button>
      </div>
    </div>
  );
}

export default function TopBar() {
  const { viewport, setViewport } = useTemplatesEditorViewport();

  return (
    <FloatingBarGroup>
      <FloatingBar>
        <LanguageSwitcher />
      </FloatingBar>
      <FloatingBar className="border bg-muted">
        <ViewportSwitcher value={viewport} onChange={setViewport} />
      </FloatingBar>
    </FloatingBarGroup>
  );
}
